/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import React, {  } from 'react';

import { IFieldInputProps } from '@ngt/forms-core';
import { IAdditionalFormInputProps } from './Input';
import useErrorStyling, { ErrorClassMapping, ErrorStylingType } from '../../../hooks/form/useErrorStyling';
import { DateValidationError, DatePicker as MuiDatePicker, DatePickerProps as MuiDatePickerProps, PickersDay, PickersLayout, dateCalendarClasses, dayCalendarSkeletonClasses, pickersCalendarHeaderClasses, pickersDayClasses } from '@mui/x-date-pickers'
import { DateTime } from 'luxon';
import { FormError } from '../../../errors';
// @ts-ignore
import { PickerChangeHandlerContext } from '@mui/x-date-pickers/internals/hooks/usePicker/usePickerValue.types';
import { Popper, TextFieldProps, buttonBaseClasses, formLabelClasses, inputAdornmentClasses, inputBaseClasses, outlinedInputClasses, styled } from '@mui/material';
import FormErrorType from '@/types/form/FormErrorType';
import { PickersModalDialog, PickersPopper } from '@mui/x-date-pickers/internals';
import { calendarPickerClasses } from '@mui/lab';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

type DatePickerProps = Partial<MuiDatePickerProps<any>> & IFieldInputProps<string, any, FormError, string> & IAdditionalFormInputProps

export interface IDatePickerProps extends DatePickerProps {
    fullWidth?: boolean;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledPickersLayout = styled(PickersLayout)(({
    theme
}) => ({
    [`& .${pickersCalendarHeaderClasses.root}`]: {
        margin: theme.spacing(2, 0, 1, 0),
        padding: theme.spacing(0, 1.5, 0, 3)
    },
    [`& .${dateCalendarClasses.root}`]: {
        width: theme.spacing(40),
        maxHeight: theme.spacing(44.75)
    },
    [`& .${pickersDayClasses.root}`]: {
        width: theme.spacing(4.5),
        height: theme.spacing(4.5)
    },
    [`& .MuiDayCalendar-slideTransition`]: {
        minHeight: theme.spacing(30)
    },
    [`& .MuiDayCalendar-weekDayLabel`]: {
        width: theme.spacing(4.5),
        height: theme.spacing(4.5)
    },
}))

const StyledMuiDatePicker = styled(MuiDatePicker)(({
    theme, 
    slotProps: {
        textField: {
            //@ts-ignore-next-line
            margin
        }
    } = {}
}) => ({
    marginTop: margin === 'normal' ?
        theme.spacing(2) :
        margin === 'dense' ?
            theme.spacing(1) :
            theme.spacing(0),
    marginBottom: margin === 'normal' ?
        theme.spacing(1) :
        margin === 'dense' ?
            theme.spacing(0.5) :
            theme.spacing(0),
    [`& .${inputBaseClasses.root}`]: {
        padding: theme.spacing(0)
    },
    [`& .${inputBaseClasses.input}`]: {
        padding: theme.spacing(2, 2)
    },
    [`& .${formLabelClasses.root}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(2)}) scale(1)`
    },
    [`& label.${formLabelClasses.focused}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(-1.25)}) scale(1)`
    },
    [`& label.${formLabelClasses.filled}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(-1.25)}) scale(1)`
    },
    [`& fieldset.${outlinedInputClasses.notchedOutline}`]: {
        padding: theme.spacing(0, 1),

        '& legend': {
            fontSize: '1em',
            '& span': {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1)
            }
        }
    },
    [`& div.${inputAdornmentClasses.positionEnd}`]: {
        [`& .${buttonBaseClasses.root}`]: {
            marginRight: 0
        }
    }
}))

/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const DatePicker: React.FunctionComponent<IDatePickerProps> = ({
    FieldInputProps: { state: { value }, actions: {  update, blur, focus, touch } },
    fullWidth,
    onChange,
    sx,
    hasErrors,
    errors,
    errorType,
    slotProps,
    format,
    slots,
    ...rest
}) => {
    const [open, setOpen] = React.useState(false);

    const onChangeCombined = React.useCallback((date: DateTime | null, context: PickerChangeHandlerContext<DateValidationError>) => {        
        if (onChange) {
            onChange(date, context);
        }

        update(date?.toISO({ includeOffset: false }) as any, true, true, true);
    }, [onChange, update]);

    const textFieldProps: Partial<TextFieldProps> = slotProps?.textField as any;

    const onFocusCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        textFieldProps?.onFocus?.(event);

        focus();
    }, [focus, textFieldProps?.onFocus]);

    const onBlurCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        textFieldProps?.onBlur?.(event);

        blur();
        touch();
    }, [blur, touch, textFieldProps?.onBlur]);

    const errorStyling = useErrorStyling(errorType, ErrorStylingType.Input);

    const onOpen = React.useCallback(() => {
        setOpen(true);
        focus();
    }, [setOpen, focus]);

    const onClose = React.useCallback(() => {
        setOpen(false);
        //blur();
    }, [setOpen, blur]);

    

    const derivedValue = React.useMemo(() => {
        if (!value) {
            return null;
        }

        return DateTime.fromISO(value);
    }, [value])

    return (
        <StyledMuiDatePicker
            {...rest as any}
            format={format === undefined ? 'dd/MM/yyyy' : format}
            onOpen={onOpen}
            onClose={onClose}
            value={derivedValue}
            onChange={onChangeCombined}
            slotProps={{
                ...slotProps,
                textField: {
                    ...textFieldProps,
                    fullWidth: textFieldProps?.fullWidth ?? (fullWidth === undefined ? true : fullWidth),
                    onBlur: onBlurCombined,
                    onFocus: onFocusCombined,
                    sx: {...textFieldProps?.sx, ...errorStyling},
                    error: hasErrors,
                    helperText: (hasErrors || textFieldProps.helperText) && (
                            <>
                                {textFieldProps.helperText && (<span className={ErrorClassMapping[FormErrorType.HelperText]}>{textFieldProps.helperText}</span>)} 
                                {errors && errors.length > 0 && errors?.map((error, i) => (<span className={ErrorClassMapping[error.type!]} key={i}>{error.message}</span>))}
                            </>
                        )
                }
            }}
            slots={{
                ...slots,
                layout: slots?.layout ?? StyledPickersLayout
            }}
        />
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default DatePicker;
